<template>
  <ValidationObserver
    ref="form"
    v-slot=""
    tag="form"
    class="event-location insicht insicht--fade-up"
    @submit.prevent="onSubmit"
    novalidate
  >
    <div class="formbody--narrow">
      <h1
        class="event-location__title"
        v-html="$t('eventlocation.mainTitle')"
      />
      <div
        class="event-location__text"
        v-html="$t('eventlocation.mainText')"
      />
    </div>

    <div class="formbody--narrow">
      <GeneralMessage
        v-if="formResponse.message"
        ref="message"
        :message="formResponse.message"
        :type="formResponse.type"
        @close="formResponse.message = ''"
      />
    </div>

    <div
      v-if="fieldSections.length > 1"
      ref="progress"
      class="form-progress"
    >
      <div class="form-progress__container--narrow">
        <progress
          class="form-progress__bar"
          :max="fieldSections.length"
          :value="currentSection + 1"
        />
      </div>
    </div>

    <template v-for="(section, i) in fieldSections">
      <section
        v-if="currentSection >= i"
        :key="i"
        ref="section"
        class="form-section"
      >
        <div class="formbody--narrow">
          <h2
            v-if="section.title"
            class="form-section__title"
            v-sanitize="section.title"
          />
          <div
            v-if="section.text"
            class="form-section__text"
            v-sanitize="section.text"
          />
        </div>

        <template v-if="section.name === 'date'">
          <div class="formbody--slim">
            <ValidationProvider
              class="input-wrapper"
              name="eventType"
              rules=""
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
            >
              <FormInput
                type="text"
                :placeholder="$t('eventlocation.eventTypeLabel')"
                :label="$t('eventlocation.eventTypeLabel')"
                :labelOption="$t('forms.labels.optional')"
                name="eventType"
                v-model="formData.eventType"
                :error="errors.length > 0"
                :disabled="formLocked"
              />
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
              <span
                class="additional-info"
                v-html="$t('eventlocation.eventTypeHint')"
              />
            </ValidationProvider>
            <ValidationProvider
              class="input-wrapper"
              name="startDate"
              rules="required"
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ required: $t('forms.errors.date') }"
            >
              <FormInputDate
                :placeholder="$t('eventlocation.dateLabel')"
                :label="$t('eventlocation.dateLabel')"
                name="startDate"
                v-model="formData.startDate"
                :initialValue="formData.startDate"
                min-date="tomorrow"
                required
                :disabled="formLocked"
              />
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
            </ValidationProvider>
            <ValidationProvider
              class="input-wrapper"
              name="time"
              rules="required"
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ required: $t('forms.errors.date') }"
            >
              <TimePicker
                v-model="formData.time"
                :times="timeOptions"
                :label="$t('forms.labels.time')"
                required
              />
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
            </ValidationProvider>
            <ValidationProvider
              class="input-wrapper"
              name="participants"
              rules="required"
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
            >
              <FormInputRange
                v-model.number="formData.participants"
                :prefix="$t('eventlocation.participantsLabel')"
                :min="locationEnhanced.participantsMin || 1"
                :max="locationEnhanced.participantsMax || 50"
                :step="1"
                required
              />
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
            </ValidationProvider>
            <ValidationProvider
              class="input-wrapper"
              name="noteDate"
              rules=""
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ }"
            >
              <FormInputTextarea
                :placeholder="$t('eventlocation.noteLabelDate')"
                :label="$t('eventlocation.noteLabelDate')"
                :labelOption="$t('forms.labels.optional')"
                name="noteDate"
                v-model="formData.noteDate"
                :disabled="formLocked"
                no-float
              />
            </ValidationProvider>
          </div>
        </template>
        <template v-if="section.name === 'menu'">
          <div
            v-if="coursesEnhanced.length"
            class="formbody--normal"
          >
            <Collapsible
              v-for="category in coursesEnhanced"
              :key="category.id"
              class="event-location__category"
              initial-open
              ref="courses"
            >
              <template v-slot:trigger="{ isOpen }">
                <h3
                  class="event-location__category-title"
                >
                  <span v-html="category.name"/>
                  <small
                    v-if="!category.optionsRequired"
                    v-html="$t('forms.labels.optional')"
                  />
                </h3>
                <div
                  v-if="errors[category.id]"
                  class="event-location__category-error"
                  v-html="errors[category.id]"
                />
              </template>
              <ul
                class="event-location__menu"
              >
                <li
                  v-for="child in category.options"
                  :key="child.uuid"
                  class="event-location__menu-item"
                  :class="{
                    'event-location__menu-item--checked': quantities[child.uuid] > 0
                  }"
                >
                  <div
                    class="event-location__menu-item-label"
                  >
                    <figure
                      class="event-location__menu-item-image"
                      v-if="child.image"
                    >
                      <img
                        class="event-location__menu-item-image-element event-location__menu-item-image-element--wide"
                        :src="child.image"
                        :alt="child.name"
                        loading="lazy"
                      >
                    </figure>
                    <div class="event-location__menu-item-content">
                      <span
                        class="event-location__menu-item-name"
                        v-html="child.name"
                      />
                      <div
                        class="event-location__menu-item-description"
                        v-html="child.description"
                      />
                      <div class="event-location__menu-item-price">
                        <span
                          class="event-location__menu-item-price-current"
                          v-html="$options.filters.fwgpricecent(child.price, 'html')"
                        />
                      </div>
                      <QuantityCounter
                        v-if="!category.calculateForAll"
                        :quantity="quantities[child.uuid] || 0"
                        base-class="event-location__menu-item-quantity"
                        :max="formData.participants"
                        @sub="onSubCourseOption(child, category)"
                        @add="onAddCourseOption(child, category)"
                        @update="onUpdateCourseOption(child, category, $event)"
                        :locked="formLocked"
                        writable
                      />
                      <div
                        v-else
                        class="event-location__menu-item-selection"
                      >
                        <Button
                          class="event-location__menu-item-button"
                          :level="!!quantities[child.uuid] ? 'primary' : 'outline'"
                          :text="!!quantities[child.uuid] ? $t('eventlocation.selectedLabel') : $t('eventlocation.selectLabel')"
                          @click.stop.prevent="onSelectCourseOption(child, category)"
                          :disabled="formLocked"
                          block
                          small
                        />
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </Collapsible>
          </div>
          <div class="formbody--slim spacing--top-2">
            <ValidationProvider
              class="input-wrapper"
              name="noteCourses"
              rules=""
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ }"
            >
              <FormInputTextarea
                :placeholder="$t('eventlocation.noteLabelMenu')"
                :label="$t('eventlocation.noteLabelMenu')"
                :labelOption="$t('forms.labels.optional')"
                name="noteCourses"
                v-model="formData.noteCourses"
                :disabled="formLocked"
                no-float
              />
            </ValidationProvider>
          </div>
        </template>
        <template v-if="section.name === 'drinks'">
          <div
            v-if="locationEnhanced.drinks"
            class="formbody--normal"
          >
            <Collapsible
              v-for="(category) in locationEnhanced.drinks"
              :key="category.id"
              class="event-location__category"
              :initial-open="false"
            >
              <template slot="trigger">
                <h3
                  class="event-location__category-title"
                >
                  <span v-html="category.name"/>
                </h3>
              </template>
              <ul
                class="event-location__drinks"
              >
                <li
                  v-for="(item, index) in category.options"
                  :key="`${category.id}-${index}`"
                  class="event-location__drinks-item"
                >
                  <span
                    class="event-location__drinks-item-name"
                    v-html="item.name"
                  />
                  <span
                    class="event-location__drinks-item-price"
                    v-html="formatDrinkPrice(item)"
                  />
                </li>
              </ul>
            </Collapsible>
          </div>
          <div class="formbody--slim spacing--top-2">
            <ValidationProvider
              class="input-wrapper"
              name="noteDrinks"
              rules=""
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ }"
            >
              <FormInputTextarea
                :placeholder="$t('eventlocation.noteLabelDrinks')"
                :label="$t('eventlocation.noteLabelDrinks')"
                :labelOption="$t('forms.labels.optional')"
                name="noteDrinks"
                v-model="formData.noteDrinks"
                :disabled="formLocked"
                no-float
              />
            </ValidationProvider>
          </div>
        </template>
        <template v-if="section.name === 'other'">
          <div class="formbody--slim">
            <FormInputCheckboxes
              class="event-location__checkboxes"
              :options="specialsOptions"
              v-model="formData.specials"
              vertical
            />
          </div>
        </template>
        <template v-if="section.name === 'details'">
          <div class="formbody--slim">
            <ValidationObserver
              class="input-wrapper"
              tag="div"
              v-slot="{ validated, invalid, errors }"
            >
              <div class="input-group">
                <ValidationProvider
                  class="input-group__span-6"
                  name="firstName"
                  rules="required"
                  v-slot="{ errors, invalid }"
                  tag="div"
                  :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                >
                  <FormInput
                    type="text"
                    :placeholder="$t('forms.labels.firstname')"
                    :label="$t('forms.labels.firstname')"
                    name="firstName"
                    v-model="formData.firstName"
                    :error="errors.length > 0"
                    :disabled="formLocked"
                    required
                  />
                </ValidationProvider>
                <ValidationProvider
                  class="input-group__span-6"
                  name="lastName"
                  rules="required"
                  v-slot="{ errors, invalid }"
                  tag="div"
                  :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                >
                  <FormInput
                    type="text"
                    :placeholder="$t('forms.labels.lastname')"
                    :label="$t('forms.labels.lastname')"
                    name="lastName"
                    v-model="formData.lastName"
                    :error="errors.length > 0"
                    :disabled="formLocked"
                    required
                  />
                </ValidationProvider>
              </div>
              <div
                class="input-error input-group__span-12"
                v-if="invalid && validated"
                v-html="combineErrors(errors)"
              />
            </ValidationObserver>
            <ValidationProvider
              class="input-wrapper"
              name="company"
              rules=""
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ }"
            >
              <FormInput
                type="text"
                :placeholder="$t('forms.labels.company')"
                :label="$t('forms.labels.company')"
                :labelOption="$t('forms.labels.optional')"
                name="company"
                v-model="formData.company"
                :disabled="formLocked"
              />
            </ValidationProvider>
            <ValidationObserver
              class="input-wrapper"
              tag="div"
              v-slot="{ validated, invalid, errors }"
            >
              <div class="input-group">
                <ValidationProvider
                  class="input-group__span-9"
                  name="street"
                  rules=""
                  v-slot="{ errors, invalid }"
                  tag="div"
                  :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                >
                  <FormInput
                    type="text"
                    :placeholder="$t('forms.labels.street')"
                    :label="$t('forms.labels.street')"
                    :labelOption="$t('forms.labels.optional')"
                    name="street"
                    v-model="formData.street"
                    :error="errors.length > 0"
                    :disabled="formLocked"
                  />
                </ValidationProvider>
                <ValidationProvider
                  class="input-group__span-3"
                  name="streetNumber"
                  rules=""
                  v-slot="{ errors, invalid }"
                  tag="div"
                  :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                >
                  <FormInput
                    type="text"
                    :placeholder="$t('forms.labels.number')"
                    :label="$t('forms.labels.number')"
                    :labelOption="$t('forms.labels.optional')"
                    name="streetNumber"
                    v-model="formData.streetNumber"
                    :error="errors.length > 0"
                    :disabled="formLocked"
                  />
                </ValidationProvider>
              </div>
              <div
                class="input-error input-group__span-12"
                v-if="invalid && validated"
                v-html="combineErrors(errors)"
              />
            </ValidationObserver>
            <ValidationObserver
              class="input-wrapper"
              tag="div"
              v-slot="{ validated, invalid, errors }"
            >
              <div class="input-group">
                <ValidationProvider
                  class="input-group__span-3"
                  name="zip"
                  rules="digits:4"
                  v-slot="{ errors, invalid }"
                  tag="div"
                  :customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
                >
                  <FormInput
                    ref="zip"
                    type="text"
                    v-model="formData.postcode"
                    @keydown="onZipKeydown"
                    @input="onZipInput"
                    :placeholder="$t('forms.labels.zip')"
                    :label="$t('forms.labels.zip')"
                    :labelOption="$t('forms.labels.optional')"
                    name="zip"
                    :minlength="4"
                    :maxlength="4"
                    inputmode="decimal"
                    pattern="[0-9]{4}"
                    :error="errors.length > 0"
                    :disabled="formLocked"
                  />
                </ValidationProvider>
                <ValidationProvider
                  class="input-group__span-9"
                  name="city"
                  rules=""
                  v-slot="{ errors, invalid }"
                  tag="div"
                  :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                >
                  <FormInput
                    type="text"
                    :placeholder="$t('forms.labels.city')"
                    :label="$t('forms.labels.city')"
                    :labelOption="$t('forms.labels.optional')"
                    name="city"
                    v-model="formData.city"
                    :error="errors.length > 0"
                    :disabled="formLocked"
                  />
                </ValidationProvider>
              </div>
              <div
                class="input-error input-group__span-12"
                v-if="invalid && validated"
                v-html="combineErrors(errors)"
              />
            </ValidationObserver>
            <ValidationProvider
              class="input-wrapper spacing--top-2"
              name="email"
              rules="required|email"
              v-slot="{ errors, invalid, validated }"
              tag="div"
              :customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
            >
              <FormInput
                type="email"
                autocomplete="email"
                :placeholder="$t('forms.labels.email')"
                :label="$t('forms.labels.email')"
                name="email"
                v-model="formData.email"
                :error="errors.length > 0"
                :disabled="formLocked"
                required
              />
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
            </ValidationProvider>
            <ValidationProvider
              class="input-wrapper"
              name="phoneNumber"
              rules="required|phone"
              v-slot="{ errors, invalid, validated }"
              tag="div"
              :customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
            >
              <FormInput
                type="tel"
                :placeholder="$t('forms.labels.phone')"
                :label="$t('forms.labels.phone')"
                name="phoneNumber"
                v-model="formData.phoneNumber"
                :error="errors.length > 0"
                :disabled="formLocked"
                required
              />
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
            </ValidationProvider>
            <ValidationProvider
              class="input-wrapper spacing--top-2"
              name="noteDetails"
              rules=""
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ }"
            >
              <FormInputTextarea
                :placeholder="$t('eventlocation.noteLabelDetails')"
                :label="$t('eventlocation.noteLabelDetails')"
                :labelOption="$t('forms.labels.optional')"
                name="noteDetails"
                v-model="formData.noteDetails"
                :disabled="formLocked"
                no-float
              />
            </ValidationProvider>

            <ValidationProvider
              :rules="{ required: { allowFalse: false } }"
              name="acceptTerms"
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ required: $t('eventlocation.errorTerms') }"
            >
              <FormInputCheckbox
                name="terms"
                value="acceptTerms"
                v-model="formData.acceptTerms"
                required
                immediate
                block
              >
                <span v-html="$t('eventlocation.termsLabel')"/>
              </FormInputCheckbox>
              <!-- TODO: this url should be dynamic -->
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
            </ValidationProvider>

            <ValidationProvider
              :rules="{ required: { allowFalse: false } }"
              name="acceptPrivacy"
              tag="div"
              v-slot="{ invalid, errors, validated }"
              :customMessages="{ required: $t('eventlocation.errorPrivacy') }"
            >
              <FormInputCheckbox
                name="terms"
                value="acceptPrivacy"
                v-model="formData.acceptPrivacy"
                required
                immediate
                block
              >
                <span v-html="$t('eventlocation.privacyLabel')"/>
              </FormInputCheckbox>
              <!-- TODO: this url should be dynamic -->
              <span
                class="input-error"
                v-if="invalid && validated"
                v-html="errors[0]"
              />
            </ValidationProvider>
          </div>
        </template>

      </section>
      <div class="formbody--narrow">
        <Button
          v-if="fieldSections.length && currentSection === i && currentSection < fieldSections.length - 1"
          level="primary"
          :text="$t('forms.buttons.next')"
          type="button"
          @click.stop.prevent="onNextSection(i)"
        />
      </div>
    </template>

    <div class="formbody--narrow">
      <Button
        v-if="currentSection === fieldSections.length - 1"
        level="primary"
        :text="$t('forms.buttons.send')"
        type="submit"
      />
    </div>

    <div class="event-location__footer">
      <div class="event-location__footer-wrapper">
        <div class="event-location__footer-details">
          <div class="event-location__footer-item">
            <div
              class="event-location__footer-item-label"
              v-html="$t('eventlocation.locationPriceLabel')"
            />
            <div
              class="event-location__footer-item-value"
              v-html="$options.filters.fwgpricecent(locationPrice, 'short')"
            />
          </div>
          <div class="event-location__footer-item">
            <div
              class="event-location__footer-item-label"
              v-html="$t('eventlocation.participantsLabel')"
            />
            <div
              class="event-location__footer-item-value"
              v-html="formData.participants"
            />
          </div>
          <div class="event-location__footer-item">
            <div
              class="event-location__footer-item-label"
              v-html="$t('eventlocation.menuPriceLabel')"
            />
            <div
              class="event-location__footer-item-value"
              v-html="`Ø ${$options.filters.fwgpricecent(averageMenuPrice, 'short')}`"
            />
          </div>
          <div class="event-location__footer-item">
            <div
              class="event-location__footer-item-label"
              v-html="$t('eventlocation.menuTotalLabel')"
            />
            <div
              class="event-location__footer-item-value"
              v-html="$options.filters.fwgpricecent(totalMenuPrice, 'short')"
            />
          </div>
        </div>
        <div class="event-location__footer-total">
          <div class="event-location__footer-item">
            <div
              class="event-location__footer-item-label"
              v-html="$t('eventlocation.totalLabel')"
            />
            <div
              class="event-location__footer-item-value"
              v-html="$options.filters.fwgpricecent(total, 'short')"
            />
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from "vuex"
import GenericMixin from '@/mixins/GenericVueMixin';
import scrrroll from 'scrrroll'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputCheckboxes from "@/components/FormInputCheckboxes"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputDate from "@/components/FormInputDate"
import FormInputRadio from "@/components/FormInputRadio"
import FormInputRange from "@/components/FormInputRange"
import FormInputSelect from "@/components/FormInputSelectCustom"
import FormInputTextarea from "@/components/FormInputTextarea"
import QuantityCounter from '@/components/QuantityCounter';
import TimePicker from "@/components/TimePicker"
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import Collapsible from '@/components/Collapsible'
import { contaoService } from '@/service/index'
import { dayjsLocal } from '@/helpers/dayjs'
import { switchDateFormat } from '@/utils/formatDate'

export default {
  mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputCheckboxes,
		FormInputCheckbox,
    FormInputDate,
    FormInputRadio,
    FormInputRange,
    FormInputSelect,
    FormInputTextarea,
    TimePicker,
		Button,
		GeneralMessage,
		Collapsible,
    QuantityCounter,
	},
  props: {},
  data() {
    return {
      location: this.$root.options.location,
      redirect: this.$root.options.redirect,
      formData: {
        acceptPrivacy: false,
        acceptTerms: false,
        city: '',
        company: '',
        email: '',
        eventType: '',
        firstName: '',
        lastName: '',
        noteCourses: '',
        noteDate: '',
        noteDetails: '',
        noteDrinks:'' ,
        participants: 0,
        phoneNumber: '',
        postcode: '',
        specials: [],
        startDate: '',
        street: '',
        streetNumber: '',
        time: '',
      },
      errors: {},
      buttonText: '',
      currentSection: 0,
      formResponse: {
				type: 'info',
				message: '',
			},
      quantities: {},
    }
  },
  computed: {
    ...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
    formLocked() {
      return this.loading
    },
    fieldSections() {
      const sections = [
        {
          name: 'date',
          title: this.$te('eventlocation.dateTitle') ? this.$t('eventlocation.dateTitle') : '',
          text: this.$te('eventlocation.dateText') ? this.$t('eventlocation.dateText') : '',
        }
      ]

      if (this.locationEnhanced?.courses?.length) {
        sections.push({
          name: 'menu',
          title: this.$te('eventlocation.menuTitle') ? this.$t('eventlocation.menuTitle') : '',
          text: this.$te('eventlocation.menuText') ? this.$t('eventlocation.menuText') : '',
        })
      }

      if (this.locationEnhanced?.drinks?.length) {
        sections.push({
          name: 'drinks',
          title: this.$te('eventlocation.drinksTitle') ? this.$t('eventlocation.drinksTitle') : '',
          text: this.$te('eventlocation.drinksText') ? this.$t('eventlocation.drinksText') : '',
        })
      }

      if (this.locationEnhanced?.specials?.length) {
        sections.push({
          name: 'other',
          title: this.$te('eventlocation.otherTitle') ? this.$t('eventlocation.otherTitle') : '',
          text: this.$te('eventlocation.otherText') ? this.$t('eventlocation.otherText') : '',
        })
      }

      sections.push({
        name: 'details',
        title: this.$te('eventlocation.detailsTitle') ? this.$t('eventlocation.detailsTitle') : '',
        text: this.$te('eventlocation.detailsText') ? this.$t('eventlocation.detailsText') : '',
      })

      return sections
    },
    locationEnhanced() {
      return this.enhancePrice(this.location ?? {})
    },
    timeOptions() {
      let options = []

      if (!this.location || !Array.isArray(this.location.timeslots)) {
        return options
      }

      options = this.location.timeslots

      return options
    },
    specialsOptions() {
      return this.locationEnhanced?.specials?.map(special => {
        return {
          value: special.uuid,
          label: special.name,
          description: special.description
        }
      }) || []
    },
    coursesEnhanced() {
      return this.locationEnhanced?.courses
        ?.sort((a, b) => a.sorting - b.sorting) || []
    },
    selectedItemsByCategory() {
			return this.locationEnhanced?.courses
        ?.map(category => {
          const options = category.options
            .filter(p => this.quantities[p.uuid])
            .map(p => ({
              ...p,
              quantity: this.quantities[p.uuid]
            }))
          return {
            ...category,
            options,
          }
        })
        .filter(category => category.options.length);
		},
		averageMenuPrice() {
      const quantity = this.selectedItemsByCategory?.reduce((a, c) => {
        return a + c.options
          .filter(o => o.quantity)
          .reduce((t, o) => {
            return t + o.quantity
          }, 0)
      }, 0) ?? 0


      return (this.totalMenuPrice || 0) / quantity
		},
		totalMenuPrice() {
      return this.selectedItemsByCategory?.reduce((a, c) => {
        const price = c.options
          .filter(o => o.quantity)
          .reduce((t, o) => {
            return Math.round(t + (o.price * o.quantity))
          }, 0)

        return a + price
      }, 0) ?? 0
		},
		locationPrice() {
			return this.locationEnhanced?.price ?? 0;
		},
		total() {
			return this.locationPrice + this.totalMenuPrice;
		},
  },
  watch: {
    'formData.participants'(newVal) {
      this.coursesEnhanced.forEach(category => {
        if (category.calculateForAll) {
          category.options.forEach(option => {
            if (this.quantities[option.uuid] > 0) {
              Vue.set(this.quantities, option.uuid, newVal)
            }
          })
        }
      })
      this.validateSelection()
    }
  },
  methods: {
    onSubCourseOption(child, category) {
			let quantity = Math.max((this.quantities[child.uuid] ?? 0) - 1, 0);
			Vue.set(this.quantities, child.uuid, quantity);
      this.validateSelection(category)
		},
		onAddCourseOption(child, category) {
			let quantity = (this.quantities[child.uuid] ?? 0) + 1;
			Vue.set(this.quantities, child.uuid, quantity);
      this.validateSelection(category)
		},
    onUpdateCourseOption(child, category, quantity) {
      Vue.set(this.quantities, child.uuid, quantity);
      this.validateSelection(category)
    },
    onSelectCourseOption(child, category) {
      if (category.calculateForAll) {
        // Deselect all other options in this category
        category.options.forEach(option => {
          if (option.uuid !== child.uuid) {
            Vue.set(this.quantities, option.uuid, 0)
          }
        })

        if (this.quantities[child.uuid] > 0) {
          Vue.set(this.quantities, child.uuid, 0)
        } else {
          // Select this option for ALL participants
          Vue.set(this.quantities, child.uuid, this.formData.participants)
        }

      }
      this.validateSelection(category)
    },
    combineErrors(errors) {
      const errorMessages = Object.values(errors).filter(value => !!value.join('').trim()).flat()
      return (Array.from(new Set(errorMessages))).join(' ')
    },
    formatDrinkPrice(item) {
      const output = [
        this.$options.filters.fwgpricecent(item.price)
      ]

      if (item.pricePer) {
        output.push(item.pricePer)
      }

      return output.join(' / ')
    },
    enhancePrice(item) {
      return Object.entries(item).reduce((newItem, [key, value]) => {
        let newValue
        if (key === 'price') {
          newValue = value * 100
        } else if (Array.isArray(value)) {
          newValue = value.map(this.enhancePrice)
        } else if (typeof value === 'object') {
          newValue = this.enhancePrice(value)
        } else {
          newValue = value
        }
        newItem[key] = newValue

        return newItem
      }, {})
    },
    validateSelection(category) {
      let categories = this.locationEnhanced?.courses

      if (category) {
        categories = categories?.filter(c => c.id === category.id)
      }

			let error = this.$refs.courses?.length && categories?.reduce((acc, category) => {
				const options = category.options.map(option => option.uuid)
				const count = options.reduce((acc, option) => {
					return (this.quantities[option] ?? 0) + acc
				}, 0)
        let error = ''

        if (category.optionsRequired && count !== this.formData.participants) {
          acc = true

          if (category.calculateForAll) {
            error = this.$t('eventlocation.courseRequiredError')
          } else {
            error = this.$t('eventlocation.courseMinError')
          }

        }

        if (!category.optionsRequired && count > this.formData.participants) {
          acc = true
          error = this.$t('eventlocation.courseMaxError')
        }

				Vue.set(this.errors, category.id, error)
				return acc
			}, false);

			return error;
		},
    async onNextSection(index) {
			this.loading = true;
			const valid = await this.$refs.form.validate();
      const selectionError = this.validateSelection();

      if (valid && !selectionError) {
        this.currentSection = Math.min(index + 1, this.fieldSections.length - 1)
        await this.$nextTick()
        scrrroll.to(this.$refs.section[this.currentSection])
      } else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error, .event-location__category-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
			}
      this.loading = false
    },
		async onSubmit(e) {
			e.preventDefault();
			this.loading = true;

			const valid = await this.$refs.form.validate();
      const selectionError = this.validateSelection();

			if (valid && !selectionError) {
        try {
          const data = {
            ...this.formData,
            eventLocationId: this.locationEnhanced?.id,
            startDate: switchDateFormat(this.formData.startDate, '-'),
            courses: this.selectedItemsByCategory.map(c => ({
              id: c.id,
              options: c.options.reduce((a, o) => ({
                ...a,
                [o.uuid]: o.quantity
              }), {})
            }))
          }
          //console.log(JSON.stringify(data, null, 2))
          //return
					const response = await contaoService.sendEventLocationRequest({
            data,
					});

          if (this.redirect) {
            window.location.href = this.redirect
          } else {
            this.loading = false;
          }
				}
				catch(err) {
          this.isDebug() && console.error(err, err.response)

          if (err?.response?.data?.errors) {
            this.$refs.form.setErrors(err.response.data.errors)

            await this.$nextTick();
            const inputError = this.$refs.form.$el.querySelector('.input-error, .event-location__category-error');

            if (inputError) {
              scrrroll.to(inputError.parentNode)
            }
          } else if (err?.response?.data?.message) {
						this.formResponse.message = err.response.data.message
					} else {
						this.formResponse.message = this.$t('forms.errors.generic');
					}
					if (this.formResponse.message) {
            this.formResponse.type = 'error';
            this.$nextTick(() => {
              this.$refs.message.scrollTo();
            });
          }
					this.loading = false;
				}
			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
    updateScrollOffset() {
      scrrroll.offset = this.$refs?.progress?.clientHeight || 0
    },
    onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.formData, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.zip.$refs.input.value = this.formData.postcode;
			}
		},
  },
  mounted() {
    window.addEventListener('resize', this.updateScrollOffset)
    this.updateScrollOffset()
    this.formData.participants = this.location?.participantsMin ?? 1
	},
  destroyed() {
    window.removeEventListener('resize', this.updateScrollOffset)
  }
}
</script>
